import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { PhotoManager } from "@/components/ui/PhotoManager";
import { PhotoParentType, type PhotoResponse } from "@/types/photos";
import { usePhotos, useDeletePhoto, usePhotoUploadWorkflow } from "@/hooks/usePhotos";
import { useUpdateOrgAppBranding } from "@/hooks/useOrgAppBranding";
import { organizationSaveErrorMessage, throwIfProblemDetails } from "@/hooks/useOrganizations";
import {
  AppPackageFields,
  ATHLYN_APP_PACKAGE,
  OTHER_APP_PACKAGE,
  appPackageErrors,
  appPackageValueFrom,
  normalizePackage,
  selectedPackage,
  type AppPackageValue,
} from "@/components/corporate/organizations/AppPackageFields";
import {
  AppBrandingEditor,
  appBrandingColourErrors,
  type AppBrandingColourField,
  type HeaderLogoSizeControl,
  type HeaderLogoSizeState,
} from "@/components/corporate/organizations/AppBrandingEditor";
import { frameWideLogo, measureSavedWideLogo, ZOOM_MAX, type WideLogoFraming } from "@/lib/logoFraming";
import type { Organization, UpdateOrganizationRequest } from "@/types/organizations";

interface EditOrganizationProps {
  organization: Organization | null;
  open: boolean;
  onClose: () => void;
}

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "nl", label: "Dutch" },
  { value: "ar", label: "Arabic" },
];

const SQUARE_LOGO_LABEL = "Square logo — organisation picker, sign-in and placeholders";
const WIDE_LOGO_LABEL = "Wide logo — home header (optional)";
const WIDE_LOGO_HELP =
  "Any size works — empty space around the logo is removed automatically. Use 'Header logo size' above the preview to fine-tune. To change the size later, upload the logo file again. Transparent PNG recommended.";

/** Same rule as the create wizard (and the API). */
const SUBDOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
const SUBDOMAIN_REQUIRED_ERROR = "Subdomain is required";
const SUBDOMAIN_FORMAT_ERROR = "Subdomain must be alphanumeric with hyphens, cannot start/end with hyphen";
export const SUBDOMAIN_LOCKED_NOTE =
  "This company's own app has its name built in. Renaming it needs a new app release — ask engineering.";

/** What a subdomain change does — shown in the confirm before anything is saved. */
export const subdomainChangeWarnings = (next: string): string[] => [
  "Portal users must use the new address and sign in there",
  "App users stay signed in; anyone who signs out picks the company again from the list",
  `Its portal address becomes ${next}.portal.athlyn.app; the old address stops working`,
  `Its join link becomes athlyn.app/join/${next}; QR codes printed with the old name stop working`,
  "If the company signs in with Microsoft (Entra), agree the change with them first",
];

const NO_APP_PACKAGE: AppPackageValue = { choice: "", other: "", appStoreId: "" };

/** Framing waits this long after the last slider move / file pick (Save flushes it at once). */
export const FRAMING_DEBOUNCE_MS = 150;

type FramingResult = WideLogoFraming | "unavailable";

/**
 * One scheduled framing of the wide logo. `promise` resolves once with the
 * framing (or "unavailable"); awaiting it again yields the SAME File, so the
 * upload guard (File identity) recognises it on a retry. `flush` starts a
 * still-debounced framing immediately.
 */
interface FramingJob {
  source: File | string;
  zoom: number;
  promise: Promise<FramingResult>;
  flush: () => void;
}

/** Pending edits of one logo slot, as reported by its PhotoManager. */
interface LogoSlotChanges {
  current: PhotoResponse[];
  newFiles: File[];
  deletedIds: string[];
}

const NO_LOGO_CHANGES: LogoSlotChanges = { current: [], newFiles: [], deletedIds: [] };

/**
 * A slider-only resize of the saved wide logo, fixed at the first Save that
 * resolves it: delete `deletedId`, upload `file` (framed at `zoom`).
 */
interface WideLogoSwap {
  deletedId: string;
  file: File;
  zoom: number;
  trimmed: ImageData;
}

const COUNTRY_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "AU", label: "Australia" },
  { value: "SG", label: "Singapore" },
];

export function EditOrganization({ organization, open, onClose }: EditOrganizationProps) {
  const queryClient = useQueryClient();
  const { photos } = usePhotos(
    organization ? PhotoParentType.Organization : null,
    organization?.id || null
  );
  const { photos: widePhotos } = usePhotos(
    organization ? PhotoParentType.OrganizationWideLogo : null,
    organization?.id || null
  );
  const deletePhoto = useDeletePhoto();
  const { uploadPhotos } = usePhotoUploadWorkflow();

  // Track photo changes per logo slot (square = Organization, wide = OrganizationWideLogo)
  const [photoChanges, setPhotoChanges] = useState<LogoSlotChanges>(NO_LOGO_CHANGES);
  const [wideLogoChanges, setWideLogoChanges] = useState<LogoSlotChanges>(NO_LOGO_CHANGES);

  // ---- Header logo size (org-header-logo-fit, D20–D26) ----------------------
  // The wide logo the slider acts on: a picked file, else the saved logo.
  const pendingWideFile = wideLogoChanges.newFiles[0] ?? null;
  const savedWide = wideLogoChanges.current[0] ?? null;
  const savedWideUrl = savedWide?.url || null;
  const sizeSource: File | string | null = pendingWideFile ?? savedWideUrl;

  // A saved logo's current size, read once per URL (null while reading).
  const [savedMeasure, setSavedMeasure] = useState<{ url: string; zoom: number | "unavailable" } | null>(null);
  useEffect(() => {
    if (!open || !savedWideUrl) return;
    let cancelled = false;
    const done = (zoom: number | "unavailable") => {
      if (!cancelled) setSavedMeasure({ url: savedWideUrl, zoom });
    };
    measureSavedWideLogo(savedWideUrl).then(done, () => done("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [open, savedWideUrl]);
  const savedZoom = savedMeasure && savedMeasure.url === savedWideUrl ? savedMeasure.zoom : null;

  // Slider starting value: a picked file starts at 100 %; a saved, readable logo at its measured size.
  const startZoom: number | null = pendingWideFile
    ? ZOOM_MAX
    : typeof savedZoom === "number"
      ? savedZoom
      : null;

  // The slider value belongs to the source it was moved for; a new source starts afresh.
  const [zoomState, setZoomState] = useState<{ source: File | string; value: number } | null>(null);
  const zoom = zoomState && zoomState.source === sizeSource ? zoomState.value : (startZoom ?? ZOOM_MAX);

  // What needs framing: a picked file (always — trimming applies at any size), or
  // a saved readable logo whose slider moved away from its starting value.
  const framingSource: File | string | null = pendingWideFile
    ? pendingWideFile
    : savedWideUrl && typeof savedZoom === "number" && zoom !== savedZoom
      ? savedWideUrl
      : null;
  const framingZoom = framingSource ? zoom : null;

  // A slider-only swap fixed by a Save. Once its delete has gone through, the
  // photo list refetches without the old logo and the framing job above is
  // dropped — so the swap is reused from here, not re-derived, on every retry
  // until the wide slot's save completes (cleared then, and on close). The ref
  // is what Save reads; the state is set only once its delete has gone through,
  // and keeps the half-saved swap on screen instead of "Add a wide logo".
  const wideSwapRef = useRef<WideLogoSwap | null>(null);
  const [wideSwap, setWideSwap] = useState<WideLogoSwap | null>(null);
  // Shown only when the old logo has left the list and nothing was picked since.
  const strandedSwap = !pendingWideFile && !savedWide ? wideSwap : null;

  const framingJobRef = useRef<FramingJob | null>(null);
  const [framing, setFraming] = useState<{ source: File | string; zoom: number; result: FramingResult } | null>(null);

  useEffect(() => {
    if (!framingSource || framingZoom === null) {
      framingJobRef.current = null;
      return;
    }
    const source = framingSource;
    const target = framingZoom;
    let cancelled = false;
    let started = false;
    let resolve!: (result: FramingResult) => void;
    const promise = new Promise<FramingResult>((r) => (resolve = r));
    const run = () => {
      if (started) return;
      started = true;
      clearTimeout(timer);
      frameWideLogo(source, target)
        .catch((): FramingResult => "unavailable")
        .then((result) => {
          resolve(result);
          if (!cancelled) setFraming({ source, zoom: target, result });
        });
    };
    const timer = setTimeout(run, FRAMING_DEBOUNCE_MS);
    framingJobRef.current = { source, zoom: target, promise, flush: run };
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [framingSource, framingZoom]);

  // The latest finished framing of the logo being framed (shown in the preview).
  const shownFraming =
    framing && framingSource && framing.source === framingSource && framing.result !== "unavailable"
      ? framing.result
      : null;
  const framingUnavailable =
    !!framing && !!framingSource && framing.source === framingSource && framing.result === "unavailable";

  const headerLogoSizeState: HeaderLogoSizeState = strandedSwap
    ? "swap-pending"
    : !sizeSource
    ? "no-wide-logo"
    : pendingWideFile
      ? framingUnavailable
        ? "unframeable"
        : "ready"
      : savedZoom === null
        ? "measuring"
        : savedZoom === "unavailable" || framingUnavailable
          ? "saved-unreadable"
          : "ready";

  const handleZoomChange = useCallback(
    (value: number) => {
      if (sizeSource) setZoomState({ source: sizeSource, value });
    },
    [sizeSource],
  );

  const headerLogoSize: HeaderLogoSizeControl = {
    value: strandedSwap ? strandedSwap.zoom : zoom,
    onChange: handleZoomChange,
    state: headerLogoSizeState,
    trimmed: strandedSwap ? strandedSwap.trimmed : (shownFraming?.trimmed ?? null),
  };

  /**
   * The wide slot's changes as they will be saved, after any in-flight or
   * debounced framing has finished:
   * - picked file ⇒ the framed file in its place (the original if it can't be framed);
   * - slider-only change on the saved logo ⇒ delete it, upload the framed one
   *   (a swap whose delete already went through is reused as is, so a retry
   *   uploads the same File once and never deletes again);
   * - otherwise the slot's changes unchanged.
   */
  const resolveWideLogoChanges = async (): Promise<LogoSlotChanges> => {
    const swap = wideSwapRef.current;
    if (!pendingWideFile && swap && deletedPhotoIdsRef.current.has(swap.deletedId)) {
      return { current: wideLogoChanges.current, deletedIds: [swap.deletedId], newFiles: [swap.file] };
    }
    const job = framingJobRef.current;
    let result: FramingResult | null = null;
    if (job) {
      job.flush();
      result = await job.promise;
    }
    const framed = result && result !== "unavailable" ? result.file : null;
    if (pendingWideFile) {
      const replacement = framed && job?.source === pendingWideFile ? framed : pendingWideFile;
      return {
        ...wideLogoChanges,
        newFiles: wideLogoChanges.newFiles.map((file) => (file === pendingWideFile ? replacement : file)),
      };
    }
    if (savedWide && result && result !== "unavailable" && job?.source === savedWideUrl) {
      const fixed: WideLogoSwap = { deletedId: savedWide.id, file: result.file, zoom: job.zoom, trimmed: result.trimmed };
      wideSwapRef.current = fixed;
      return { current: wideLogoChanges.current, deletedIds: [fixed.deletedId], newFiles: [fixed.file] };
    }
    return wideLogoChanges;
  };

  // Photo work already applied in this dialog session. If the second save
  // (app-branding) fails the dialog stays open, and a retry must not upload the
  // same logo twice or re-delete a photo that is already gone. Shared by both
  // logo slots: the keys (File identity, photo id) are unique across slots.
  const uploadedFilesRef = useRef<WeakSet<File>>(new WeakSet());
  const deletedPhotoIdsRef = useRef<Set<string>>(new Set());

  // In-flight guard: set synchronously at the top of handleSubmit so a double
  // click cannot start a second save while the first is still running (the
  // photo phase has no pending mutation to disable the button).
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // App-branding fields are saved by their own endpoint
  // (PUT /organizations/{id}/app-branding) — the main PUT never touches them.
  const [appBranding, setAppBranding] = useState<{
    accentColor: string;
    employeeAppBrandingEnabled: boolean;
  }>({ accentColor: "", employeeAppBrandingEnabled: false });

  // App package + App Store ID + subdomain (org-app-store-links). The saved values are what
  // "changed?" is measured against; only changed fields are sent (null/absent = unchanged).
  const [appPackage, setAppPackage] = useState<AppPackageValue>(NO_APP_PACKAGE);
  const [subdomain, setSubdomain] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ subdomain?: string; appScheme?: string; appStoreId?: string }>({});
  // The new subdomain awaiting the confirm (null = no confirm open).
  const [pendingSubdomain, setPendingSubdomain] = useState<string | null>(null);
  const savedPackage = normalizePackage(organization?.appScheme);
  const knownPackage = organization?.isKnownApp ? savedPackage : null;
  // The saved flags hold while the package is unchanged (or set to Athlyn). Choosing another app in
  // this dialog locks the subdomain and branding for the save: the API refuses a switch + rename in
  // one request, and the new app won't take the Athlyn app's branding.
  const keepsAthlynFlags = (value: AppPackageValue) => {
    const pkg = selectedPackage(value);
    if (!pkg) return value.choice !== OTHER_APP_PACKAGE; // "Other…" not typed yet: already another app
    return pkg === savedPackage || pkg === ATHLYN_APP_PACKAGE;
  };
  const packageKeepsFlags = keepsAthlynFlags(appPackage);
  const canChangeSubdomain = organization?.canChangeSubdomain === true && packageKeepsFlags;
  const usesAthlynApp = organization?.usesAthlynApp === true && packageKeepsFlags;

  const [formData, setFormData] = useState<UpdateOrganizationRequest>({
    name: "",
    defaultLanguage: "",
    primaryColor: "",
    secondaryColor: "",
    taxRegistrationNumber: "",
    address: "",
    postalCode: "",
    state: "",
    city: "",
    country: "",
    isWhiteLabel: false,
  });

  // Initialize the form ONCE per dialog open (and again only if a different
  // organization is shown). A refetch while the dialog is open — e.g. after the
  // first save invalidates the query — hands us a new object with the server's
  // values; re-syncing from it would wipe the user's unsaved edits.
  const initialisedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initialisedForRef.current = null;
      // Retry guards only make sense within one dialog session.
      uploadedFilesRef.current = new WeakSet();
      deletedPhotoIdsRef.current = new Set();
      // Unsaved header-logo sizing does not outlive the dialog session.
      setZoomState(null);
      setFraming(null);
      setSavedMeasure(null);
      wideSwapRef.current = null;
      setWideSwap(null);
      return;
    }
    if (organization && initialisedForRef.current !== organization.id) {
      initialisedForRef.current = organization.id;
      setFormData({
        name: organization.name || "",
        defaultLanguage: organization.defaultLanguage || "",
        primaryColor: organization.primaryColor || "",
        secondaryColor: organization.secondaryColor || "",
        taxRegistrationNumber: organization.taxRegistrationNumber || "",
        address: organization.address || "",
        postalCode: organization.postalCode || "",
        state: organization.state || "",
        city: organization.city || "",
        country: organization.country || "",
        isWhiteLabel: organization.isWhiteLabel ?? false,
      });
      setAppBranding({
        accentColor: organization.accentColor || "",
        employeeAppBrandingEnabled: organization.employeeAppBrandingEnabled ?? false,
      });
      setAppPackage(appPackageValueFrom(organization.appScheme, organization.appStoreId, organization.isKnownApp === true));
      setSubdomain(organization.subdomain || "");
      setFieldErrors({});
      setPendingSubdomain(null);
    }
  }, [organization, open]);

  const updateAppBranding = useUpdateOrgAppBranding(organization?.id);

  const updateOrganization = useMutation({
    mutationFn: async (data: UpdateOrganizationRequest) => {
      if (!organization) throw new Error("No organization selected");
      const response = await apiRequest("PUT", `/organizations/${organization.id}`, data);
      // A format error arrives as a bare ValidationProblemDetails, which the parser passes through.
      throwIfProblemDetails(response);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/organizations/${organization?.id}`] });
      queryClient.invalidateQueries({ queryKey: ["/organizations"] });
      // Success toast + close happen in handleSubmit, after the app-branding save also succeeds.
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: organizationSaveErrorMessage(error, "Failed to update organization"),
        variant: "destructive",
      });
    },
  });

  /** Applies one logo slot's pending deletes then uploads, skipping work an earlier attempt already did. */
  const saveLogoSlot = async (parentType: PhotoParentType, changes: LogoSlotChanges) => {
    for (const photoId of changes.deletedIds) {
      if (deletedPhotoIdsRef.current.has(photoId)) continue;
      await deletePhoto.mutateAsync(photoId);
      deletedPhotoIdsRef.current.add(photoId);
      if (wideSwapRef.current?.deletedId === photoId) setWideSwap(wideSwapRef.current);
    }

    const filesToUpload = changes.newFiles.filter((file) => !uploadedFilesRef.current.has(file));
    if (filesToUpload.length > 0 && organization) {
      await uploadPhotos(parentType, organization.id, filesToUpload);
      filesToUpload.forEach((file) => uploadedFilesRef.current.add(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    // Validate required fields
    if (!formData.name?.trim()) {
      toast({
        title: "Validation Error",
        description: "Organization name is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.defaultLanguage?.trim()) {
      toast({
        title: "Validation Error",
        description: "Default language is required",
        variant: "destructive",
      });
      return;
    }

    const colourErrors = appBrandingColourErrors({
      primaryColor: formData.primaryColor,
      secondaryColor: formData.secondaryColor,
      accentColor: appBranding.accentColor,
    });
    if (Object.keys(colourErrors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Fix the highlighted colour fields: use a 6-digit hex colour like #1C3446.",
        variant: "destructive",
      });
      return;
    }

    // Subdomain (only when it may change and did change) + the app fields.
    const nextSubdomain = subdomain.trim().toLowerCase();
    const subdomainChanged = canChangeSubdomain && nextSubdomain !== (organization?.subdomain ?? "").trim().toLowerCase();
    const errors: typeof fieldErrors = { ...appPackageErrors(appPackage, knownPackage) };
    if (subdomainChanged) {
      if (!nextSubdomain) errors.subdomain = SUBDOMAIN_REQUIRED_ERROR;
      else if (!SUBDOMAIN_PATTERN.test(nextSubdomain)) errors.subdomain = SUBDOMAIN_FORMAT_ERROR;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Fix the highlighted fields.",
        variant: "destructive",
      });
      return;
    }

    if (subdomainChanged) {
      // Nothing is saved until the confirm says so; Cancel sends nothing.
      setPendingSubdomain(nextSubdomain);
      return;
    }
    await save(null);
  };

  /** The app fields as the PUT sends them: only what changed (absent = unchanged). */
  const changedAppFields = (): Pick<UpdateOrganizationRequest, "appScheme" | "appStoreId"> => {
    const fields: Pick<UpdateOrganizationRequest, "appScheme" | "appStoreId"> = {};
    const pkg = selectedPackage(appPackage);
    if (pkg && pkg !== savedPackage) fields.appScheme = pkg;
    const storeId = appPackage.appStoreId.trim();
    if (storeId !== (organization?.appStoreId ?? "").trim()) fields.appStoreId = storeId; // "" clears it
    return fields;
  };

  const save = async (newSubdomain: string | null) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      // Any in-flight / debounced framing finishes before anything is saved.
      const wideChangesToSave = await resolveWideLogoChanges();

      // Logos first: square, then wide (each: deletions, then uploads)
      await saveLogoSlot(PhotoParentType.Organization, photoChanges);
      await saveLogoSlot(PhotoParentType.OrganizationWideLogo, wideChangesToSave);
      // The wide slot is saved: a later retry (app-branding failed) has no swap left to finish.
      wideSwapRef.current = null;
      setWideSwap(null);

      // Clean up empty strings to undefined for optional fields
      const cleanedData: UpdateOrganizationRequest = {
        name: (formData.name ?? "").trim(),
        defaultLanguage: (formData.defaultLanguage ?? "").trim(),
        primaryColor: formData.primaryColor?.trim() || undefined,
        secondaryColor: formData.secondaryColor?.trim() || undefined,
        taxRegistrationNumber: formData.taxRegistrationNumber?.trim() || undefined,
        address: formData.address?.trim() || undefined,
        postalCode: formData.postalCode?.trim() || undefined,
        state: formData.state?.trim() || undefined,
        city: formData.city?.trim() || undefined,
        country: formData.country?.trim() || undefined,
        isWhiteLabel: formData.isWhiteLabel,
        ...(newSubdomain ? { subdomain: newSubdomain } : {}),
        ...changedAppFields(),
      };

      // Update organization data, then the app-branding fields
      await updateOrganization.mutateAsync(cleanedData);
      await updateAppBranding.mutateAsync({
        accentColor: appBranding.accentColor.trim() || null,
        employeeAppBrandingEnabled: appBranding.employeeAppBrandingEnabled,
      });

      // Invalidate both logo slots' photo queries
      queryClient.invalidateQueries({
        queryKey: ["/photos", PhotoParentType.Organization, organization?.id]
      });
      queryClient.invalidateQueries({
        queryKey: ["/photos", PhotoParentType.OrganizationWideLogo, organization?.id]
      });

      toast({
        title: "Organization updated",
        description: "Organization details have been updated successfully.",
      });
      onClose();
    } catch (error) {
      // Each mutation toasts its own failure; the dialog stays open with the values intact.
      console.error("Error updating organization:", error);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UpdateOrganizationRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBrandingColourChange = (field: AppBrandingColourField, value: string) => {
    if (field === "accentColor") {
      setAppBranding(prev => ({ ...prev, accentColor: value }));
    } else {
      handleInputChange(field, value);
    }
  };

  const isSaving =
    isSubmitting || updateOrganization.isPending || updateAppBranding.isPending || deletePhoto.isPending;

  const handlePhotoChange = useCallback((current: PhotoResponse[], newFiles: File[], deletedIds: string[]) => {
    setPhotoChanges({ current, newFiles, deletedIds });
  }, []);

  const handleWideLogoChange = useCallback((current: PhotoResponse[], newFiles: File[], deletedIds: string[]) => {
    setWideLogoChanges({ current, newFiles, deletedIds });
  }, []);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        onClose();
      }
    }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Organization</DialogTitle>
          <DialogDescription>
            Update the organization details. Required fields are marked with an asterisk (*).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  value={formData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter organization name"
                  maxLength={255}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultLanguage">Default Language *</Label>
                <Select
                  value={formData.defaultLanguage || ""}
                  onValueChange={(value) => handleInputChange("defaultLanguage", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-org-subdomain">Subdomain</Label>
              <Input
                id="edit-org-subdomain"
                value={subdomain}
                onChange={(e) => {
                  setSubdomain(e.target.value);
                  setFieldErrors(({ subdomain: _cleared, ...rest }) => rest);
                }}
                readOnly={!canChangeSubdomain}
                autoCapitalize="none"
                spellCheck={false}
                maxLength={100}
                className={!canChangeSubdomain ? "bg-muted text-muted-foreground" : undefined}
                aria-invalid={!!fieldErrors.subdomain}
                aria-describedby={
                  !canChangeSubdomain
                    ? "edit-org-subdomain-locked"
                    : fieldErrors.subdomain
                      ? "edit-org-subdomain-error"
                      : "edit-org-subdomain-help"
                }
              />
              {fieldErrors.subdomain && (
                <p id="edit-org-subdomain-error" className="text-sm text-destructive">
                  {fieldErrors.subdomain}
                </p>
              )}
              {canChangeSubdomain ? (
                <p id="edit-org-subdomain-help" className="text-xs text-muted-foreground">
                  Portal address: {(subdomain.trim().toLowerCase() || "subdomain")}.portal.athlyn.app
                </p>
              ) : (
                <p id="edit-org-subdomain-locked" className="text-xs text-muted-foreground">
                  {SUBDOMAIN_LOCKED_NOTE}
                </p>
              )}
            </div>
          </div>

          {/* App */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">App</h3>
            <AppPackageFields
              idPrefix="edit-org"
              value={appPackage}
              onChange={(value) => {
                setAppPackage(value);
                // Another app locks the subdomain for this save: drop any typed rename.
                if (!keepsAthlynFlags(value)) {
                  setSubdomain(organization?.subdomain || "");
                  setFieldErrors(({ subdomain: _sub, appScheme: _scheme, appStoreId: _storeId, ...rest }) => rest);
                } else {
                  setFieldErrors(({ appScheme: _scheme, appStoreId: _storeId, ...rest }) => rest);
                }
              }}
              errors={{ appScheme: fieldErrors.appScheme, appStoreId: fieldErrors.appStoreId }}
              extraKnownPackage={knownPackage}
            />
          </div>

          {/* White Label */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="edit-is-white-label">White Label</Label>
              <p className="text-xs text-muted-foreground">
                Enable white labeling for this organization
              </p>
            </div>
            <Switch
              id="edit-is-white-label"
              checked={formData.isWhiteLabel ?? false}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isWhiteLabel: checked }))}
            />
          </div>

          {/* Logo */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Logo</h3>

            {organization && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <PhotoManager
                  label={SQUARE_LOGO_LABEL}
                  currentPhotos={photos}
                  maxPhotos={1}
                  maxFileSizeMB={5}
                  onChange={handlePhotoChange}
                />
                <PhotoManager
                  label={WIDE_LOGO_LABEL}
                  helpText={WIDE_LOGO_HELP}
                  currentPhotos={widePhotos}
                  maxPhotos={1}
                  maxFileSizeMB={5}
                  previewFit="contain"
                  onChange={handleWideLogoChange}
                />
              </div>
            )}
          </div>

          {/* Branding */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Branding</h3>

            <AppBrandingEditor
              idPrefix="edit-org"
              primaryColor={formData.primaryColor || ""}
              secondaryColor={formData.secondaryColor || ""}
              accentColor={appBranding.accentColor}
              employeeAppBrandingEnabled={appBranding.employeeAppBrandingEnabled}
              usesAthlynApp={usesAthlynApp}
              onColourChange={handleBrandingColourChange}
              onEnabledChange={(enabled) =>
                setAppBranding(prev => ({ ...prev, employeeAppBrandingEnabled: enabled }))
              }
              logoFile={photoChanges.newFiles[0] ?? null}
              logoUrl={photoChanges.current[0]?.url ?? null}
              wideLogoFile={strandedSwap?.file ?? shownFraming?.file ?? pendingWideFile}
              wideLogoUrl={wideLogoChanges.current[0]?.url ?? null}
              organizationName={formData.name || organization?.name}
              headerLogoSize={organization ? headerLogoSize : undefined}
            />
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Address Information</h3>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address || ""}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Enter full address"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city || ""}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  placeholder="Enter city"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={formData.state || ""}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  placeholder="Enter state or province"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode || ""}
                  onChange={(e) => handleInputChange("postalCode", e.target.value)}
                  placeholder="Enter postal code"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country || ""}
                  onValueChange={(value) => handleInputChange("country", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Tax Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Tax Information</h3>

            <div className="space-y-2">
              <Label htmlFor="taxRegistrationNumber">Tax Registration Number</Label>
              <Input
                id="taxRegistrationNumber"
                value={formData.taxRegistrationNumber || ""}
                onChange={(e) => handleInputChange("taxRegistrationNumber", e.target.value)}
                placeholder="Enter tax registration number"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Updating..." : "Update Organization"}
            </Button>
          </DialogFooter>
        </form>

        <AlertDialog
          open={pendingSubdomain !== null}
          onOpenChange={(isOpen) => {
            if (!isOpen) setPendingSubdomain(null);
          }}
        >
          <AlertDialogContent data-testid="subdomain-change-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Change the subdomain to {pendingSubdomain}?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <ul className="list-disc space-y-1 pl-5 text-left text-sm text-muted-foreground">
                  {subdomainChangeWarnings(pendingSubdomain ?? "").map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const next = pendingSubdomain;
                  setPendingSubdomain(null);
                  if (next) void save(next);
                }}
              >
                Change subdomain
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

export default EditOrganization;